// ══════════════════════════════════════════════════
// MARK: Setup
// ──────────────────────────────────────────────────
// #═#═#═#═#═# Imports #═#═#═#═#═#
import type IconElement from '../icon/class';
import type { ShadowElements, IconDefinition, Icons } from './types';
import { CSSRuleSet } from '../../ts/class/style/exportlist';
import style from './style.scss?inline';


// ══════════════════════════════════════════════════
// MARK: Searchbar Component
// ──────────────────────────────────────────────────
class SearchbarElement extends HTMLElement {
	// #═#═#═#═#═# Properties #═#═#═#═#═#
		// #════ Static ════#
	/** Observed Attributes */
	static observedAttributes: string[]	=	[
		'state',
		'button-label',
		'button-labelledby',
		'input-label',
		'input-labelledby',

		'disabled',

		'placeholder',
		'value',
		'action',
	];

	/** Icon Characters */
	static icons: Icons = {
		open: {
			character: 'f002',
			font: 'fas'
		},
		close: {
			character: 'f00d',
			font: 'fas'
		}
	};


		// #════ Initialized ════#
	/** Shadow DOM */
	public shadowRoot: ShadowRoot	=	this.attachShadow({mode: 'open'});
	/**	Stylesheet */
	private stylesheet: CSSRuleSet	=	new CSSRuleSet(style);
	/** List of Sub Elements */
	private shadowElements!: ShadowElements;
	/** Icon States */
	protected icon	=	{
		open: this.__openIcon,
		close: this.__closeIcon
	};


		// #════ Uninitialized ════#
	__icons?: Partial<Icons>;



	// #═#═#═#═#═# Constructor #═#═#═#═#═#
	constructor() {
		// #════ Parent ════#
		super();


		// #════ Properties ════#
		this.shadowRoot.adoptedStyleSheets	=	[this.stylesheet.getStylesheet()];


		// #════ Mehtods ════#
		this.render();
		this.getSubElements();
	}


	// #═#═#═#═#═# Build Shadow DOM #═#═#═#═#═#
	/**
	 * Builds the Shadow DOM
	 */
	private render(
	): void {
		this.shadowRoot.innerHTML = `
			<div part="collapsible">
				<form part="form" action="${this.getAttribute("action") || ""}" method="GET" role="search">
					<input type="search" part="input" aria-label="${this.getAttribute("input-label") || "Search for:"}" name="s" />
				</form>
			</div>
			<slot name="button">
				<button>
					<slot name="button-icon" part="button-icon-slot">
						<cvh-icon data-element="icon" part="button-icon"></cvh-icon>
					</slot>
					<slot name="button-label" part="button-label-slot">
						<span data-element="text" part="button-label">${this.getAttribute('button-label') || 'Search'}</span>
					</slot>
				</button>
			</slot>
		`;
	}


	// #═#═#═#═#═# Get Elements from Shadow DOM #═#═#═#═#═#
	/**
	 * Gets the Sub Elements from the Shadow DOM
	 */
	private getSubElements(
	): void {
		// #════ Get Elements ════#
		const shadow: ShadowElements	=	{} as ShadowElements;

		shadow.input	=	this.shadowRoot.querySelector('input') as HTMLInputElement;
		shadow.button	=	this.shadowRoot.querySelector('slot[name="button"] > button') as HTMLButtonElement;
		shadow.label	=	shadow.button.querySelector('span') as HTMLSpanElement;
		shadow.icon		=	shadow.button.querySelector('cvh-icon') as IconElement;
		shadow.wrap		=	this.shadowRoot.querySelector('[part="collapsible"]') as HTMLDivElement;
		shadow.form		=	this.shadowRoot.querySelector('form') as HTMLFormElement;


		// #════ Set Shadow Elements ════#
		this.shadowElements	=	shadow;
	}

	
	// #═#═#═#═#═# Attributes Changed #═#═#═#═#═#
	/**
	 * Lifecycle Method: Attributes Changed
	 */
	attributeChangedCallback(
		name: string,
		oldValue: string,
		newValue: string
	): void {
		// #════ Guard ════#
		// Bail if no change was made
		if(oldValue === newValue) {
			return;
		}


		// #════ Set Attribute ════#
		switch(name) {
			// State changes need special handling to open/close the searchbar
			case 'state':
				switch(newValue) {
					case 'static':
					case 'open':
						this.#open();
						return;

					case 'closed':
						this.#close();
						return;

					default:
						return;
				}

			// Accessibility attributes for input and button
			case 'input-label':
				this.shadowElements.input.setAttribute('aria-label', newValue);
				break;
	
			case 'input-labelledby':
				this.shadowElements.input.setAttribute('aria-labelledby', newValue);
				break;

			case 'button-label':
				this.shadowElements.label.innerText	=	newValue;
				break;
			
			case 'button-labelledby':
				this.shadowElements.label.setAttribute('aria-labelledby', newValue);
				break;

			// Disabled needs special handling to properly disable the input and button elements
			case 'disabled':
				if(newValue === null || newValue === 'false') {
					this.disabled	=	false;
				} else {
					this.disabled	=	true;
				}
				break;

			// Because the 'action' attribute is on the form element, we separately handle it
			case 'action':
				this.shadowElements.form.setAttribute('action', newValue);
				break;

			// Attributes like 'placeholder' and 'value' are directly passed to the input element
			// so we don't need to handle them here specifically.
			// They are handled in the default case.
			default:
				this.shadowElements.input.setAttribute(name, newValue);
				break;
		}
	}


	// #═#═#═#═#═# Connected Callback #═#═#═#═#═#
	/**
	 * Lifecycle Method: Connected Callback
	 */
	connectedCallback(
	): void {
		// #════ Set Element State ════#
		if(!this.hasAttribute('state')) {
			this.setAttribute('state', 'static');
		}


		// #════ Events ════#
		this.connectEvents();
	}


	// #═#═#═#═#═# Connect Events #═#═#═#═#═#
	/**
	 * Handles the Eventlisteners for all sub elements
	 */
	private connectEvents(
	): void {
		// #════ Button ════#
		this.shadowElements.button.addEventListener('click', event => {
			event.preventDefault();

			if(this.getAttribute('state') !== 'static') {
				this.toggle();
			}

			else {
				this.submit();
			}
		});


		// #════ Input ════#
		this.shadowElements.form.addEventListener('submit', event => {
			event.preventDefault();
			this.submit();
		});
	}


	// #═#═#═#═#═# Toggle #═#═#═#═#═#
	/**
	 * Toggles the open/close state of the Searchbar
	 */
	public toggle(
	): void {
		// #════ Guard ════#
		if(this.static) {
			console.warn('Searchbar is in static mode. Cannot toggle state.');
			return;
		}


		// #════ Toggle State ════#
		this.open = !this.open;
	}


	// #═#═#═#═#═# Open #═#═#═#═#═#
	/**
	 * Open	the Searchbar
	 */
	#open(
	): void {
		// #════ Form Accessibilty ════#
		this.shadowElements.form.setAttribute('aria-expanded', 'true');
		this.shadowElements.form.removeAttribute('aria-hidden');
		this.shadowElements.input.removeAttribute('tab-index');


		// #════ With State ════#
		if(this.state === 'static') {
			// +──── Set Icon ────+
			this.shadowElements.icon.setIcon(this.icon.open);
		}

		else {
			// +──── Set Icon ────+
			this.shadowElements.icon.setIcon(this.icon.close);
		
			// +──── Focus Input ────+
			this.shadowElements.input.focus();
			this.shadowElements.input.select();
		}
	}


	// #═#═#═#═#═# Close #═#═#═#═#═#
	/**
	 * Close the Searchbar
	 */
	#close(
	): void {
		// #════ Form Accessibilty ════#
		this.shadowElements.form.setAttribute('aria-expanded', 'false');
		this.shadowElements.form.setAttribute('aria-hidden', '');
		this.shadowElements.input.setAttribute('tab-index', '-1');


		// #════ Set Icon ════#
		this.shadowElements.icon.setIcon(this.icon.open);


		// #════ Blur ════#
		this.shadowElements.input.blur();
	}


	// #═#═#═#═#═# Submit #═#═#═#═#═#
	/**
	 * Submits the Searchbar
	 */
	public submit(
	): void {
		if(this.shadowElements.input.value === '') {
			this.shadowElements.input.focus();
		}

		else {
			this.shadowElements.form.submit();
		}
	}


	//
	// MARK: Accessors
	//
	// #═#═#═#═#═# State #═#═#═#═#═#
	public get state(): string | null {
		return this.getAttribute('state');
	}

	public set state(value: string | null) {
		if(value) {
			this.setAttribute('state', value);
		}

		else {
			this.setAttribute('state', 'static');
		}
	}


	// #═#═#═#═#═# Open State #═#═#═#═#═#
	public get open(): boolean {
		const state = this.state;

		// Both 'open' and 'static' are considered open states
		return state === 'open' || state === 'static';
	}

	public set open(value: boolean) {
		// Changing state is handled by the attributeChangedCallback
		// so we dont neet do call the #open() or #close() methods here.
		this.state = (value)? 'open' : 'closed';
	}


	// #═#═#═#═#═# Static State #═#═#═#═#═#
	public get static(): boolean {
		return this.state === 'static';
	}

	public set static(value: boolean) {
		this.state = (value)? 'static' : 'open';
	}


	// #═#═#═#═#═# Disabled #═#═#═#═#═#
	public get disabled(): boolean {
		return this.hasAttribute('disabled');
	}

	public set disabled(value: boolean) {
		if(value) {
			if(!this.hasAttribute('disabled'))
				this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}

		this.shadowElements.input.disabled	=	value;
		this.shadowElements.button.disabled	=	value;
	}


	//
	// MARK: Icon Management
	//
	// #═#═#═#═#═# Open Icon #═#═#═#═#═#
	get __openIcon() {
		if(this.__icons?.open) {
			return this.__icons.open;
		}

		return SearchbarElement.icons.open;
	}

	set __openIcon(value: IconDefinition|undefined) {
		if(this.__icons === undefined) {
			this.__icons	=	{};
		}

		if(value !== undefined) {
			this.__icons.open	=	value;
		} else {
			delete this.__icons.open;
		}
	}


	// #═#═#═#═#═# Close Icon #═#═#═#═#═#
	get __closeIcon() {
		if(this.__icons?.close) {
			return this.__icons.close;
		}

		return SearchbarElement.icons.close;
	}

	set __closeIcon(value: IconDefinition|undefined) {
		if(this.__icons === undefined) {
			this.__icons	=	{};
		}

		if(value !== undefined) {
			this.__icons.close	=	value;
		} else {
			delete this.__icons.close;
		}
	}
}


// ══════════════════════════════════════════════════
// MARK: Initialisation
// ──────────────────────────────────────────────────
window.customElements.define('cvh-searchbar', SearchbarElement);
